'use client';
import { toPublicContentOwner } from '@eliadmin/core/src/api/content-fields/public-content-fields';
import { ContentFields } from '@eliadmin/core/src/components/content-fields/content-fields';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { ImageType } from '@eliadmin/core/src/api/api-types';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';

import { fetchCourses, type CourseDetail } from '@eliadmin/core/src/api/courses/coursers-api';
import { fetchDynamicSections } from '@eliadmin/core/src/api/slugs/website-content-api';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LuAlarmClock } from 'react-icons/lu';
import { HtmlDescription } from '../shared/html-description';
import { AddCourseToFavoritesButton } from './add-course-to-favorites-button';
import { InfoSection } from '../homepage/info-section';
import { Heading } from '../shared/heading';
import { CourseCard } from './course-card';
import { useQuery } from '@tanstack/react-query';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { CourseLevel } from './course-level';

type CourseDetailBoughtProps = {
  course: CourseDetail;
  /** Nie je k dispozícii pri kurzoch zdarma, ktoré si môže pozrieť aj neprihlásený návštevník. */
  me?: Customer;
};

interface Lector {
  image: ImageType;
  name: string;
  slug: string;
}

export function CourseDetailBought({ course }: CourseDetailBoughtProps) {
  const locale = useLocaleStore(s => s.currentLocale);

  const { t } = useTranslations();

  const { data: infoSections = { data: [] } } = useQuery({
    queryKey: ['SECTIONS', locale],
    queryFn: () => fetchDynamicSections({ locale, sectionIds: [2] }),
  });

  const { data: allCourses = { data: [] } } = useQuery({
    queryKey: ['ONLINE_CLASSES', locale, course.course_type?.id],
    queryFn: () =>
      fetchCourses({
        locale,
        query: {
          images: false,
          results: 6,
          page: 1,
          where: { courseTypeId: { in: [course.course_type!.id] } },
        },
      }),
    enabled: !!course.course_type?.id,
  });

  const courses = allCourses.data.filter(c => c.id !== course.id);

  const video = course?.chapters[0]?.video_url;

  console.log(course?.chapters);

  return (
    <>
      <div className="relative flex aspect-video max-h-[720px] w-full items-center justify-center overflow-hidden">
        {video && (
          <iframe
            src={`https://player.vimeo.com/video/${video}?badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0`}
            width="1280"
            height="720"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            className="aspect-video w-full overflow-hidden"
            title={course.name}
          />
        )}
      </div>
      <section className={cn('custom-section', 'py-12')}>
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-start justify-start gap-8 md:flex-row md:justify-between lg:gap-16',
          )}
        >
          <div className="flex w-full max-w-[881px] flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
            {!!course.levels && (
              <div className="flex w-full items-center justify-start gap-2">
                <CourseLevel course={course as any} />
              </div>
            )}

            <h1
              className={cn(
                'w-full text-left text-3xl font-medium text-black sm:text-4xl lg:text-5xl xl:text-6xl',
                'font-heading tracking-tight',
              )}
            >
              {course.name}
            </h1>
            <div className="flex w-full flex-wrap items-center justify-start gap-2 sm:gap-3 lg:gap-6">
              <div className="flex w-full items-center justify-start gap-2 sm:gap-3 md:gap-4">
                {course.categories && course.categories.length > 0 && (
                  <CourseIconLabel label={course?.categories[0]?.name} image="/images/ballet.svg" />
                )}
                <CourseIconLabel
                  label={`${course.duration} min.`}
                  icon={
                    <LuAlarmClock className="min-h-4 min-w-4 text-primary sm:min-h-5 sm:min-w-5 lg:min-h-6 lg:min-w-6" />
                  }
                />
              </div>
            </div>
            <HtmlDescription
              content={course.short_description ?? course.description}
              className="w-full text-left text-black/70"
              size="base"
            />
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-8 sm:gap-6 md:max-w-[340px]">
            {course.lecturers && course.lecturers.length > 0 && (
              <div className="flex w-full flex-col items-start justify-start gap-3">
                <p className="text-base font-bold text-black/70">{t('text1948')}</p>
                {course.lecturers.map(lector => (
                  <CourseLector
                    key={lector.slug}
                    lector={{
                      image: lector.image! ?? { url: '/images/placeholder.png' },
                      name: lector.name,
                      slug: lector.slug,
                    }}
                  />
                ))}
              </div>
            )}
            <div className="flex w-full flex-col items-start justify-start gap-2">
              <AddCourseToFavoritesButton
                course={{
                  slug: course.slug,
                  is_favorite: !!course?.product.is_favorite,
                }}
                showText
                className={{
                  button:
                    'flex h-[52px] w-full flex-row-reverse items-center justify-center border border-border no-underline hover:bg-neutral-50',
                  innerContainer: 'w-full flex-row-reverse items-center justify-center gap-2',
                  text: 'text-base font-bold no-underline',
                }}
              />
            </div>
          </div>
        </div>
      </section>
      {infoSections && infoSections.data.length > 0 && (
        <InfoSection
          section={infoSections.data[0] ?? {}}
          className={{ section: 'pt-6 sm:pt-8 lg:pt-10' }}
        />
      )}
      <section className={cn('custom-section', 'py-12 sm:py-16 lg:py-20')}>
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-center justify-center gap-6 sm:gap-12 lg:gap-16',
          )}
        >
          <Heading
            subtitle="DANCE CLASSES"
            title={t('text1949')}
            className={{
              container: 'w-full max-w-[700px] items-center justify-center text-center',
            }}
          />
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3">
            {courses.map(course => (
              <CourseCard course={course} key={course.name} />
            ))}
          </div>
        </div>
      </section>
      <ContentFields owner={toPublicContentOwner(course)} module="courses" />
    </>
  );
}

function CourseLector({ lector }: { lector: Lector }) {
  return (
    <Link
      prefetch={false}
      href={lector.slug}
      className="flex w-full items-center justify-start gap-3"
    >
      <Image
        src={lector.image.url}
        alt={resolveImageAlt(lector?.image, lector?.name)}
        width={0}
        height={0}
        className="max-h-14 min-h-14 min-w-14 max-w-14 rounded-full border-2 border-white object-cover object-center sm:max-h-16 sm:min-h-16 sm:min-w-16 sm:max-w-16 lg:max-h-[72px] lg:min-h-[72px] lg:min-w-[72px] lg:max-w-[72px]"
        sizes="72px"
      />
      <p className="text-base font-normal text-black">{lector.name}</p>
    </Link>
  );
}

const CourseIconLabel = ({
  icon,
  image,
  label,
}: {
  icon?: React.ReactNode;
  image?: string;
  label: string;
}) => {
  return (
    <div className="flex items-center justify-start gap-2">
      {icon ? (
        icon
      ) : (
        <Image
          src={image ?? '/images/placeholder.png'}
          alt={label}
          width={0}
          height={0}
          className="min-h-4 min-w-4 sm:min-h-5 sm:min-w-5 lg:min-h-6 lg:min-w-6"
        />
      )}
      <p className="text-xs font-normal text-black sm:text-sm lg:text-base">{label}</p>
    </div>
  );
};
